import React from 'react';
import { Page } from 'framework7-react';
import PropTypes from 'prop-types';
import FormGenerator from '../../FormBuilder/FormGenerator';
import { formOptionHtmlId, formWidgetHtmlId } from '../../FormBuilder/utils';
import II18 from '../../translate/II18';
import { gaPageView } from '../../helpers/Analytics';
import { FORMELEMENTS_TEXTAREA } from '../../FormBuilder/FormElements';
import { ZoomButtons } from '../../desktop/Elements';
import {
  defineElementLocationInViewport,
  DOCUMENT_EXTERNAL_EVENTS,
  POSITION_INSIDE,
  shallowEqual
} from '../../helpers/utils';
import { PdfElementsNavigation } from './PdfElementsNavigation';
import Spinner from '../../helpers/Spinner';
import { getPdfImageProps, isBoxesWidget } from '../../helpers/formUtils';
import withFormsCommonUI from '../../formsCommon/desktop/withFormsCommonUI';
import withESignature from '../../formsCommon/withESignature';
import { DocumentRejectedMessage } from '../../common/DocumentRejectedMessage';

class PDFFormContent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      imagesReady: false,
      showActiveAnimation: false,
      treatAsStarted:
        this.props.returnFromQuickForm || props.documentWasVisited,
      nextElementLocation: POSITION_INSIDE,
      pageScrolledAfterDone: props.formValid,
      navMarkerButtonVisible: true,
      zoom: 60,
      scaleFactor: props.document.attachment.map(() => ({
        scaleX: 1,
        scaleY: 1
      }))
    };
    this.imageCounter = 0;
  }

  componentDidMount() {
    gaPageView('PDF Form');

    let thiss = this;

    if (window.attachEvent) {
      window.attachEvent('onresize', thiss.onResize);
    } else if (window.addEventListener) {
      window.addEventListener('resize', thiss.onResize, false);
    }

    this.navButtonCron = setInterval(this.updateNavButtonState, 100);

    this.moveToFirstRequiredElement(true);
  }

  componentWillUnmount() {
    document.removeEventListener('resize', this.onResize);
    clearInterval(this.navButtonCron);
  }

  getNextRequiredElement = () => {
    const err = this.props.getFirstErrorElement();
    return err && err.element;
  };

  updateNavButtonState = () => {
    const navMarkerButton = document.getElementById('btnNavMarker');
    const scrollContainer = document.getElementById(this.props.scrollContainer);

    if (this.props.formValid && !this.state.pageScrolledAfterDone) {
      this.setState({
        pageScrolledAfterDone: true
      });
    }

    if (navMarkerButton) {
      const navMarkerButtonVisible =
        navMarkerButton.offsetTop >= scrollContainer.scrollTop &&
        navMarkerButton.offsetTop <
          scrollContainer.scrollTop + scrollContainer.clientHeight;

      let nextElementLocation = this.state.nextElementLocation;

      const nextRequiredElement = this.getNextRequiredElement();
      if (nextRequiredElement) {
        nextElementLocation = defineElementLocationInViewport(
          nextRequiredElement.id,
          this.props.scrollContainer
        );
      }
      if (
        this.state.navMarkerButtonVisible !== navMarkerButtonVisible ||
        this.state.nextElementLocation !== nextElementLocation
      ) {
        this.setState({
          navMarkerButtonVisible,
          nextElementLocation
        });
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    this.props.checkSignal(DOCUMENT_EXTERNAL_EVENTS.NEXT_ELEMENT, () => {
      if (this.props.formValid || this.props.scanEnded) {
        this.props.onFormDone(true);
      } else {
        this.moveToFirstRequiredElement();
      }
    });

    if (this.props.moveToFirstError && !prevProps.moveToFirstError) {
      this.moveToFirstRequiredElement();
    }

    if (!prevState.imagesReady && this.state.imagesReady) {
      this.$f7.dialog.close();
      //this.$f7.dialog.destroy();
      this.prepareFormInputs();
      this.prepareScaleFactor();

      if (this.props.returnFromQuickForm) {
        this.$f7.dialog.alert(
          II18.get('pdf_return_from_smartfill'),
          II18.get('title_attention')
        );
      }
    }

    if (shallowEqual(prevProps.formStructure, this.props.formStructure)) {
      this.prepareFormInputs();
    }

    if (
      this.props.formValid &&
      !prevProps.formValid &&
      this.props.canAutoDoneOnBecomeValid() &&
      !this.props.scanEnded
    ) {
      this.props.onFormDone(true);
    }

    if (
      this.props.documentIsQualifiedSigned &&
      !prevProps.documentIsQualifiedSigned
    ) {
      this.setState({
        refreshPdfPages: true
      });
    }

    if (this.props.signatureProcessItem && !prevProps.signatureProcessItem) {
      this.setActiveElement(this.props.signatureProcessItem, true);
    }
  }

  prepareScaleFactor = () => {
    this.setState({
      scaleFactor: this.props.document.attachment.map((item, index) =>
        this.getScaleFactor(index)
      )
    });
  };

  prepareFormInputs() {
    const thiss = this;
    thiss.props.formStructure.forEach(page => {
      page.forEach(element => {
        let input = document.getElementById(formWidgetHtmlId(element));
        if (input && !input.prepared) {
          input.prepared = true;

          // noinspection JSUnusedLocalSymbols
          input.addEventListener('focus', function(event) {
            if (
              thiss.state.activeElement &&
              thiss.state.activeElement.id !== element.id
            ) {
              thiss.setState({
                showActiveAnimation: false
              });
            }
            thiss.setActiveElement(element, false);
          });

          input.addEventListener('keydown', function(event) {
            if (
              event.keyCode === 13 &&
              element.element !== FORMELEMENTS_TEXTAREA
            ) {
              event.preventDefault();
              thiss.goNextElement();
            }
          });
        }
      });
    });
  }

  onResize = () => {
    this.forceUpdate();
    this.prepareScaleFactor();
  };

  goBack = () => {
    this.props.navigateBack();
  };

  getScaleFactor = idx => {
    const container = document.getElementById('viewerPage' + idx);
    if (container) {
      const slideSize = container.getBoundingClientRect();
      const curImgProps = getPdfImageProps(this.props.pdfMetaData[idx]);

      return {
        scaleX: (slideSize.width - 2) / curImgProps.imageWidth,
        scaleY: (slideSize.height - 2) / curImgProps.imageHeight
      };
    } else {
      return { scaleX: 1, scaleY: 1 };
    }
  };

  precalcSlideImageSize = idx => {
    const container = document.getElementById('viewerPage' + idx);
    if (container) {
      const slideSize = container.getBoundingClientRect();

      const curImgProps = getPdfImageProps(this.props.pdfMetaData[idx]);
      return {
        width: slideSize.width || '100%',
        height:
          curImgProps.imageHeight *
            (slideSize.width / curImgProps.imageWidth) || '50vh'
      };
    } else {
      return {
        width: '100%',
        height: '50vh'
      };
    }
  };

  getSlideTop = idx => {
    const container = document.getElementById('viewerPage' + idx);
    return container ? container.offsetTop : 0;
  };

  updateFormResult = (item, value, data) => {
    this.setState({
      showActiveAnimation: false,
      treatAsStarted: true,
      activeElement: this.props.updateFormResult(item, value, data)
    });
  };

  navButtonClick = () => {
    if (this.props.formValid) {
      this.props.onFormDone();
    } else this.moveToFirstRequiredElement();
  };

  moveToFirstRequiredElement = justMove => {
    // formValid ignores apiError (so Done can re-run external validation),
    // so navigate by actual formErrors — including external API errors.
    const err = this.props.getFirstErrorElement();
    if (!err?.element) {
      return;
    }
    this.props.cancelMoveToFirstError();

    this.setActiveElement(err.element, !justMove, !justMove);
    if (!justMove) {
      this.setState({
        showActiveAnimation: true,
        treatAsStarted: !justMove
      });
    }
  };

  setActiveElement = (element, centerElement, focusElement) => {
    this.setState(
      {
        activeElement: element,
        nextElementLocation: POSITION_INSIDE,
        navMarkerButtonVisible: true
      },
      () => {
        if (centerElement) {
          this.centerElement(element);
        }
        focusElement && this.focusElement();
      }
    );
  };

  centerElement = element => {
    const optionElement = isBoxesWidget(element.element);

    const input = document.getElementById(
      optionElement
        ? formOptionHtmlId(element, element.options[0])
        : formWidgetHtmlId(element)
    );
    input?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start'
    });
  };

  goNextElement = () => {
    const { pageIndex, elementIndex } = this.state.activeElement || {
      pageIndex: 0,
      elementIndex: 0
    };
    this.props.goNextElement(pageIndex, elementIndex, item => {
      item && this.setActiveElement(item, false, true);
    });
  };

  focusElement = onAfterFocus => {
    const element = this.state.activeElement;

    let input = element && document.getElementById(formWidgetHtmlId(element));
    if (input) {
      input.focus();
      onAfterFocus && onAfterFocus();
    }
  };

  onZoomChange = z => {
    this.setState(
      {
        zoom: z
      },
      () => this.prepareScaleFactor()
    );
  };

  calcElementNavPosition = widget => {
    const pageScaleY = this.state.scaleFactor[widget.pageIndex].scaleY;
    const actualWidget = isBoxesWidget(widget.element)
      ? widget.options[0]
      : widget;
    return (
      actualWidget.y * pageScaleY - 17 + (actualWidget.height / 2) * pageScaleY
    );
  };

  _getZoomWidth(zoom) {
    return zoom + '%';
  }

  render() {
    const {
      document: documentItem,
      scanEnded,
      formValid,
      formErrors,
      formResult,
      formStructure,
      pageReady
    } = this.props;

    if (!documentItem) {
      // noinspection CheckTagEmptyBody
      return <Page className={'errorPage'}></Page>;
    }

    const {
      showActiveAnimation,
      activeElement,
      imagesReady,
      scaleFactor,
      zoom,
      treatAsStarted,
      nextElementLocation,
      pageScrolledAfterDone,
      navMarkerButtonVisible
    } = this.state;

    const activeElementIndex = activeElement && activeElement.elementIndex;
    const activeElementPageIndex =
      (activeElement && activeElement.pageIndex) || 0;

    const activeElementError =
      activeElement && this.props.formErrors.get(activeElement.id);

    const nextRequiredElement = this.getNextRequiredElement();
    const nextRequiredElementError =
      nextRequiredElement && this.props.formErrors.get(nextRequiredElement.id);

    const navMarkerButtonTopPosition = activeElement
      ? this.calcElementNavPosition(activeElement)
      : 0;
    const navGoToNextButtonTopPosition = nextRequiredElement
      ? this.calcElementNavPosition(nextRequiredElement)
      : 0;

    const preparePageUrl = (index, url) => {
      if (
        this.props.qualifiedSignatureInfo &&
        this.props.qualifiedSignatureInfo.pageIdx === index
      ) {
        return this.props.qualifiedSignatureInfo.url;
      }
      return url;
    };

    return (
      <>
        {documentItem?.rejected && <DocumentRejectedMessage />}

        {imagesReady && !scanEnded && (
          <div
            style={{
              width: this._getZoomWidth(zoom),
              margin: '10px auto'
            }}
          >
            <PdfElementsNavigation
              navMarkerButtonSlideTopPosition={this.getSlideTop(
                treatAsStarted ? activeElementPageIndex : 0
              )}
              navGoToNextButtonSlideTopPosition={
                nextRequiredElement &&
                this.getSlideTop(nextRequiredElement.pageIndex)
              }
              navMarkerButtonTopPosition={navMarkerButtonTopPosition}
              navMarkerButtonElementError={activeElementError}
              navMarkerButtonElementType={
                activeElement && activeElement.element
              }
              navMarkerButtonVisible={navMarkerButtonVisible}
              formValid={formValid}
              formActionTitleKey={this.props.formActionTitleKey}
              treatAsStarted={treatAsStarted}
              pageScrolledAfterDone={pageScrolledAfterDone}
              navGoToNextButtonElementType={
                nextRequiredElement && nextRequiredElement.element
              }
              navGoToNextButtonElementError={nextRequiredElementError}
              nextElementLocation={nextElementLocation}
              navGoToNextButtonTopPosition={navGoToNextButtonTopPosition}
              navButtonClick={this.navButtonClick}
              noStartButtonMode={Boolean(documentItem.isOnlySignatures)}
            />
          </div>
        )}
        {pageReady &&
          documentItem.attachment.map((url, pageIndex) => (
            <div
              id={'viewerPage' + pageIndex}
              className={`pdfPage viewer-page ${
                documentItem.finalised ? 'finalised' : ''
              }`}
              key={pageIndex}
              style={{
                width: this._getZoomWidth(zoom),
                zIndex: activeElementPageIndex === pageIndex ? 1 : 0
              }}
            >
              {pageIndex <= this.imageCounter ? (
                <img
                  key={documentItem.id + '-' + pageIndex}
                  alt={`Page ${pageIndex + 1}`}
                  onLoad={() => {
                    this.imageCounter++;
                    this.forceUpdate();
                    this.prepareScaleFactor();

                    this.setState({
                      imagesReady: true
                    });
                  }}
                  className={'slideImage'}
                  src={preparePageUrl(pageIndex, url)}
                  style={{
                    width: '100%',
                    marginBottom: -5,
                    ...(pageIndex === this.imageCounter && {
                      ...this.precalcSlideImageSize(pageIndex),
                      background: 'white'
                    })
                  }}
                />
              ) : (
                <div
                  style={{
                    ...this.precalcSlideImageSize(pageIndex),
                    background: 'white',
                    maxWidth: '100%'
                  }}
                >
                  <Spinner />
                </div>
              )}
              <label className={'page-number'}>{pageIndex + 1}</label>

              <div className={'pdfFormSlide'}>
                {imagesReady && (
                  <FormGenerator
                    onItemClick={this.props.onFormItemClicked}
                    onItemFocus={this.props.onFormItemFocused}
                    documentName={this.props.document.name}
                    documentId={this.props.document.id}
                    showRequiredErrors={this.props.showRequiredErrors}
                    showActiveAnimation={showActiveAnimation}
                    activeElementIndex={
                      pageIndex === activeElementPageIndex
                        ? activeElementIndex
                        : -1
                    }
                    f7={this.$f7}
                    ios={this.$f7.device.ios}
                    readOnly={scanEnded}
                    desktop
                    pageNumber={pageIndex}
                    isActivePage={pageIndex === activeElementPageIndex}
                    absolutePlaceMode
                    scaleFactor={scaleFactor[pageIndex]}
                    formResult={formResult}
                    formErrors={formErrors}
                    formStructure={formStructure[pageIndex]}
                    requestedDocuments={this.props.requestedDocuments}
                    onAnyChange={this.updateFormResult}
                    dpiModifier={this.props.pdfMetaData[pageIndex].dpiModifier}
                  />
                )}
              </div>
            </div>
          ))}
        <ZoomButtons
          onChange={this.onZoomChange}
          initialZoom={zoom}
          maxZoom={120}
          minZoom={40}
          sticky
        />
      </>
    );
  }
}

PDFFormContent.propTypes = {
  canAutoDoneOnBecomeValid: PropTypes.func,
  cancelMoveToFirstError: PropTypes.func,
  moveToFirstError: PropTypes.bool,
  open: PropTypes.bool,
  showRequiredErrors: PropTypes.bool,
  formValid: PropTypes.bool,
  formActionTitleKey: PropTypes.string,
  readyToSignatureItem: PropTypes.any,
  tryBack: PropTypes.func,
  onFormItemClicked: PropTypes.func,
  onFormItemFocused: PropTypes.func,
  goNextElement: PropTypes.func,
  goPrevElement: PropTypes.func,
  goNextPage: PropTypes.func,
  goPrevPage: PropTypes.func,
  getFirstErrorElement: PropTypes.func,
  checkDataAndTryBack: PropTypes.func,
  updateFormResult: PropTypes.func,
  onStartSignatureProcess: PropTypes.func,
  generateForm: PropTypes.func,
  formErrors: PropTypes.object,
  formResult: PropTypes.object,
  pdfMetaData: PropTypes.array,
  formStructure: PropTypes.arrayOf(PropTypes.array),
  pages: PropTypes.array,
  cachedSignature: PropTypes.any,
  scrollContainer: PropTypes.string,
  signatureProcessItem: PropTypes.object,

  //withDocumentCommonPropTypes
  onDocumentDone: PropTypes.func,
  navigateBack: PropTypes.func,
  renderUserCredential: PropTypes.func,
  renderSender: PropTypes.func,
  scanEnded: PropTypes.bool,
  returnFromQuickForm: PropTypes.bool,
  documentWasVisited: PropTypes.bool,
  pageReady: PropTypes.bool,
  document: PropTypes.object,
  onFormDone: PropTypes.func,

  qualifiedSignatureInfo: PropTypes.object,
  documentIsQualifiedSigned: PropTypes.bool,
  onSignmeRegistrationClosed: PropTypes.func,
  checkSignmeAccount: PropTypes.func,
  checkSignal: PropTypes.func,
  collectBioDataConfirmed: PropTypes.bool,
  onCollectBioDataConfirmed: PropTypes.func,
  showTitleAndInfo: PropTypes.any,
  requestedDocuments: PropTypes.array
};

export default withESignature(withFormsCommonUI(PDFFormContent));
